import { useEffect, useState } from 'react';
import { Form, Field } from 'react-final-form';
import { format } from 'date-fns';
import { useI18n } from '@locales/client';

import {
  BodyTextBoldSecondary,
  BodyTextRegularSecondary,
} from '@/shared/ui/typography';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { FormField } from '@/shared/ui/form/form-field';
import { DatePicker } from '@/shared/ui/date-picker';
import { Box } from '@/shared/ui/box';
import { validateSchema } from '@/shared/libs/validate';
import { CheckboxField } from '@/shared/ui/form/checkbox-field';

import { SelectCountries } from '@/features/select-countries';
import { useFormSave } from '@/features/wallet-managment/model/hooks';
import { personalInfoSchema } from '@/features/wallet-managment/model/schemas';
import { getDefaultDate18YearsAgo } from '@/features/wallet-managment/model/utils';
import { CardForm } from '../card-form';

import { InputField, FormItem, FormGroup } from './styles';
import { WithdrawButton } from '../../styles';

const INIT_FORM = {
  fname: '',
  lname: '',
  birthday: '',
  country: '',
  city: '',
  street: '',
  zip: '',
  phone: '',
  email: '',
  save: true,
};
const KEY_SAVE_FORM = 'customer';

const FullForm = ({ handleSubmit, settings, isLocked }) => {
  const { user } = useUser();
  const t = useI18n();
  const isNext =
    settings?.fields?.length > 0 && settings?.fields?.includes('cardForm');
  const [isCardFormShow, setIsCardFormShow] = useState(false);
  const { savePay } = useFormSave(KEY_SAVE_FORM);
  const hasNonEmptyObject = savePay?.some((obj) => Object.keys(obj).length > 0);

  const defaultDate = getDefaultDate18YearsAgo();
  const [form, setForm] = useState(INIT_FORM);

  useEffect(() => {
    if (!user) return;
    const { phone, email, birthday } = user;
    setForm((res) => ({
      ...res,
      phone: phone ? phone.code + phone.value : '',
      email: email || '',
      birthday: birthday ? format(birthday, 'yyyy-MM-dd') : defaultDate,
    }));
  }, [user, defaultDate]);

  const DatePickerFieldCmp = (props) => (
    <DatePicker initialDate={form?.birthday} {...props} />
  );

  const validate = (values) => {
    const getSchema = validateSchema(personalInfoSchema(t));
    return getSchema(values);
  };

  const handleFormSubmit = () => {
    setIsCardFormShow(true);
  };

  useEffect(() => {
    if (hasNonEmptyObject && !isCardFormShow) {
      setForm({ ...savePay?.at(-1), save: true });
    }
    if (isNext) {
      setIsCardFormShow(true);
    }
  }, [isNext, savePay, isCardFormShow]);

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        initialValues={form}
        validate={validate}
        render={(props) => {
          const isDisabled = props.form.getState().invalid || isLocked;

          useEffect(() => {
            if (!isDisabled) {
              setForm(props?.values);
            }
          }, [isDisabled]);

          return (
            <FormGroup onSubmit={props.handleSubmit} $isHidden={isCardFormShow}>
              <FormItem>
                <InputField $roundTop={true} $roundBottom={true}>
                  <BodyTextRegularSecondary>
                    {t('wallet.fiat.input_fields.sections.personal_data')}
                  </BodyTextRegularSecondary>
                  <Field
                    name={'fname'}
                    component={FormField}
                    bgColor="#4B444C"
                    type={'text'}
                    placeholder={t(
                      'wallet.fiat.input_fields.fname.placeholder'
                    )}
                    placeholderColor="#AFAFAF"
                  />
                  <Field
                    name={'lname'}
                    component={FormField}
                    bgColor="#4B444C"
                    type={'text'}
                    placeholder={t(
                      'wallet.fiat.input_fields.lname.placeholder'
                    )}
                    placeholderColor="#AFAFAF"
                  />
                  <Field
                    name={'birthday'}
                    component={DatePickerFieldCmp}
                    bgColor="#4B444C"
                    type={'text'}
                    label={
                      <BodyTextBoldSecondary>
                        {t('wallet.fiat.input_fields.birthday.label')}
                      </BodyTextBoldSecondary>
                    }
                  />
                </InputField>
              </FormItem>

              <FormItem>
                <InputField $roundTop={true} $roundBottom={true}>
                  <BodyTextRegularSecondary>
                    {t('wallet.fiat.input_fields.sections.residence_address')}
                  </BodyTextRegularSecondary>
                  <Field
                    name={'country'}
                    autoComplete="off"
                    component={SelectCountries}
                    no_options={t(
                      'wallet.fiat.input_fields.country.no_options'
                    )}
                    bgColor="#4B444C"
                    placeholder={t(
                      'wallet.fiat.input_fields.country.placeholder'
                    )}
                    placeholderColor="#AFAFAF"
                    type={'text'}
                  />

                  <Field
                    name={'city'}
                    autoComplete="off"
                    component={FormField}
                    placeholder={t('wallet.fiat.input_fields.city.placeholder')}
                    placeholderColor="#AFAFAF"
                    bgColor="#4B444C"
                    type={'text'}
                  />
                  <Field
                    name={'street'}
                    autoComplete="off"
                    component={FormField}
                    placeholder={t(
                      'wallet.fiat.input_fields.street.placeholder'
                    )}
                    placeholderColor="#AFAFAF"
                    bgColor="#4B444C"
                    type={'text'}
                  />

                  <Field
                    name={'zip'}
                    autoComplete="off"
                    placeholder={t('wallet.fiat.input_fields.zip.placeholder')}
                    placeholderColor="#AFAFAF"
                    component={FormField}
                    bgColor="#4B444C"
                    type={'text'}
                  />
                </InputField>
              </FormItem>

              <FormItem>
                <Box $marginBottom="64px">
                  <InputField $roundTop={true} $roundBottom={true}>
                    <BodyTextRegularSecondary>
                      {t('wallet.fiat.input_fields.sections.contact_data')}
                    </BodyTextRegularSecondary>
                    <Field
                      name={'email'}
                      component={FormField}
                      bgColor="#4B444C"
                      type={'email'}
                      placeholder={t(
                        'profile.account.security.email.placeholder'
                      )}
                      placeholderColor="#AFAFAF"
                    />
                    <Field
                      name={'phone'}
                      autoComplete="off"
                      placeholder={t(
                        'wallet.fiat.input_fields.phone.placeholder'
                      )}
                      placeholderColor="#AFAFAF"
                      component={FormField}
                      bgColor="#4B444C"
                      type={'text'}
                    />
                  </InputField>
                  <Box $marginTop="12px">
                    <Field
                      name="save"
                      component={CheckboxField}
                      type="checkbox"
                      label={
                        <BodyTextRegularSecondary>
                          {t('wallet.fiat.input_fields.sections.save')}
                        </BodyTextRegularSecondary>
                      }
                    />
                  </Box>
                </Box>
              </FormItem>

              <Box
                $position="absolute"
                $bottom="18px"
                $borderRadius="8px"
                $opacity="1"
                $width="calc(100% - 36px)"
                $zIndex="100"
                $backgroundColor="#3D3C41"
              >
                <WithdrawButton
                  type={isNext ? 'button' : 'submit'}
                  variant={isDisabled ? 'gray' : 'orangeGradient'}
                  $isLoading={isLocked}
                  disabled={isDisabled}
                >
                  {isNext
                    ? t('wallet.fiat.input_fields.sections.submit')
                    : t('wallet.fiat.pay')}
                </WithdrawButton>
              </Box>
            </FormGroup>
          );
        }}
      />

      {isCardFormShow && (
        <CardForm
          handleSubmit={handleSubmit}
          settings={settings}
          valuesForm={form}
          isLocked={isLocked}
        />
      )}
    </>
  );
};

export { FullForm };
